import { usePathname } from "expo-router";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { StatusBannerType } from "@luna-restaurant/ui";

type StatusBannerBaseInput = {
  type: StatusBannerType;
  message: string;
  autoDismissAfterSeconds?: number;
};

type StatusBannerViewActionInput =
  | {
      onViewPress: () => void;
      viewButtonLabel: string;
    }
  | {
      onViewPress?: undefined;
      viewButtonLabel?: never;
    };

type StatusBannerDismissActionInput =
  | {
      dismissible: true;
      dismissButtonLabel: string;
    }
  | {
      dismissible?: false;
      dismissButtonLabel?: never;
    };

type StatusBannerInput = StatusBannerBaseInput &
  StatusBannerViewActionInput &
  StatusBannerDismissActionInput;

type StatusBannerState = {
  id: number;
  banner: StatusBannerInput;
};

type StatusBannerContextValue = {
  state: StatusBannerState | null;
  showStatusBanner: (input: StatusBannerInput) => void;
  clearStatusBanner: () => void;
};

const StatusBannerContext = createContext<StatusBannerContextValue | undefined>(
  undefined,
);

type StatusBannerProviderProps = {
  children: ReactNode;
};

export const StatusBannerProvider = ({
  children,
}: StatusBannerProviderProps) => {
  const [state, setState] = useState<StatusBannerState | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setState(null);
  }, [pathname]);

  const showStatusBanner = useCallback((input: StatusBannerInput) => {
    setState((current) => ({
      id: (current?.id ?? 0) + 1,
      banner: input,
    }));
  }, []);

  const clearStatusBanner = useCallback(() => {
    setState(null);
  }, []);

  const value = useMemo(
    () => ({ state, showStatusBanner, clearStatusBanner }),
    [state, showStatusBanner, clearStatusBanner],
  );

  return (
    <StatusBannerContext.Provider value={value}>
      {children}
    </StatusBannerContext.Provider>
  );
};

export const useStatusBanner = () => {
  const context = useContext(StatusBannerContext);

  if (!context) {
    throw new Error("useStatusBanner must be used within StatusBannerProvider");
  }

  return context;
};
